import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUser as setReduxUser, clearUser as clearReduxUser } from 'userSlice';

import {
  loginApi,
  registerApi,
  verifyEmailApi,
  getMeApi,
  logoutApi,
  resendOtpApi,
  requestOtpApi,
  updateProfileApi,
  changePasswordApi,
  forgotPasswordApi,
  verifyResetOtpApi,
  resetPasswordApi
} from 'utils/methods/auth';
import { setAxiosAuthToken } from 'utils/axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const reduxUser = useSelector(state => state.user.user);
  const reduxToken = useSelector(state => state.user.token);
  const [loading, setLoading] = useState(true);
  const [isLoginModalOpen, setLoginModalOpen] = useState(false);
  const [loginRedirectPath, setLoginRedirectPath] = useState(null);
  const dispatch = useDispatch();
  const isInitialized = useRef(false);

  // Initialize authentication on app load
  useEffect(() => {
    // Prevent duplicate initialization in React.StrictMode
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      
      // CRITICAL: Set the token in axios SYNCHRONOUSLY before any async operations
      // This ensures the token is available for any API calls made during component mount
      if (storedToken) {
        setAxiosAuthToken(storedToken);
      }
      
      // If we have a token in localStorage but not in Redux, sync them
      if (storedToken && !reduxToken) {
        try {
          const response = await getMeApi();
          dispatch(setReduxUser({ user: response.data.user, token: storedToken }));
        } catch (error) {
          if (error?.response?.status === 401) {
            // Token is invalid, clear everything
            localStorage.removeItem('token');
            setAxiosAuthToken(null);
            dispatch(clearReduxUser());
          }
        }
      }
      
      setLoading(false);
    };
    
    initializeAuth();
  }, []);

  // Update axios token when Redux token changes (but not on initial mount)
  useEffect(() => {
    if (reduxToken) {
      setAxiosAuthToken(reduxToken);
    }
  }, [reduxToken]);

  const login = async (emailOrUsername, password) => {
    try {
      const response = await loginApi(emailOrUsername, password);
      const { access_token, user: userData } = response.data;
      localStorage.setItem('token', access_token);
      dispatch(setReduxUser({ user: userData, token: access_token }));
      setAxiosAuthToken(access_token);
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await registerApi(userData);
      const { temp_token, user: newUser, requires_verification } = response.data;
      if (requires_verification) {
        localStorage.setItem('temp_token', temp_token);
        dispatch(setReduxUser({ user: newUser, token: null }));
        return { 
          success: true, 
          user: newUser, 
          temp_token,
          requires_verification: true 
        };
      } else {
        localStorage.setItem('token', temp_token);
        dispatch(setReduxUser({ user: newUser, token: temp_token }));
        setAxiosAuthToken(temp_token);
        return { success: true, user: newUser };
      }
    } catch (error) {
      console.error('Registration failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const verifyEmail = async (otpCode, tempToken) => {
    try {
      const tokenToUse = tempToken || localStorage.getItem('temp_token');
      if (!tokenToUse) {
        throw new Error('No temporary token found. Please try registering again.');
      }
      const response = await verifyEmailApi(otpCode, tokenToUse);
      const { access_token, user: verifiedUser } = response.data;
      localStorage.removeItem('temp_token');
      localStorage.setItem('token', access_token);
      dispatch(setReduxUser({ user: verifiedUser, token: access_token }));
      setAxiosAuthToken(access_token);
      return { success: true, user: verifiedUser, access_token };
    } catch (error) {
      console.error('Email verification failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Email verification failed' 
      };
    }
  };

  const resendOTP = async (tempToken) => {
    try {
      const tokenToUse = tempToken || localStorage.getItem('temp_token');
      if (!tokenToUse) {
        throw new Error('No temporary token found. Please try registering again.');
      }
      const response = await resendOtpApi(tokenToUse);
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Resend OTP failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to resend OTP' 
      };
    }
  };

  const requestOTP = async (email) => {
    try {
      const response = await requestOtpApi(email);
      const { temp_token, message } = response.data;
      localStorage.setItem('temp_token', temp_token);
      return { success: true, temp_token, message };
    } catch (error) {
      console.error('Request OTP failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to request OTP' 
      };
    }
  };

  const logout = async () => {
    try {
      if (reduxToken) {
        await logoutApi();
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      localStorage.removeItem('token');
      dispatch(clearReduxUser());
      setAxiosAuthToken(null);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await updateProfileApi(profileData);
  // setUser removed: Redux is source of truth
      dispatch(setReduxUser(response.data.user));
      return { success: true, user: response.data.user };
    } catch (error) {
      console.error('Profile update failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Profile update failed' 
      };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await changePasswordApi(currentPassword, newPassword);
      return { success: true };
    } catch (error) {
      console.error('Password change failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Password change failed' 
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      const response = await forgotPasswordApi(email);
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Forgot password failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to send password reset email' 
      };
    }
  };

  const verifyResetOTP = async (email, otpCode) => {
    try {
      const response = await verifyResetOtpApi(email, otpCode);
      const { reset_token } = response.data;
      localStorage.setItem('reset_token', reset_token);
      return { success: true, reset_token, message: response.data.message };
    } catch (error) {
      console.error('Reset OTP verification failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'OTP verification failed' 
      };
    }
  };

  const resetPassword = async (newPassword) => {
    try {
      const resetToken = localStorage.getItem('reset_token');
      if (!resetToken) {
        throw new Error('No reset token found. Please start the password reset process again.');
      }
      const response = await resetPasswordApi(resetToken, newPassword);
      localStorage.removeItem('reset_token');
      return { success: true, message: response.data.message };
    } catch (error) {
      console.error('Password reset failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Password reset failed' 
      };
    }
  };

  const value = {
  user: reduxUser,
  token: reduxToken,
    loading,
    login,
    register,
    verifyEmail,
    resendOTP,
    requestOTP,
    logout,
    updateProfile,
    changePassword,
    forgotPassword,
    verifyResetOTP,
    resetPassword,
  isAuthenticated: !!reduxUser,
    isLoginModalOpen,
    openLoginModal: (redirectPath = null) => {
      setLoginRedirectPath(redirectPath);
      setLoginModalOpen(true);
    },
    closeLoginModal: () => setLoginModalOpen(false),
    loginRedirectPath
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
